import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import { SERVICE_REQUEST_STATUS } from "@features/cx/shared/utils/constants";
import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AlertTriangle, ArrowDown, ArrowUp, XCircle } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useComplaintsExport,
  useCurrentComplaints,
  useUpdateComplaintStatus,
} from "../../hooks";
import { Complaint, ComplaintFilters } from "../../types";
import { ComplaintFilterPanel } from "../filters/filters";

const renderPriority = (value: string) => {
  if (value === "High") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
      </span>
    );
  }
  if (value === "Medium") {
    return (
      <span
        style={{
          color: "#d97706",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowUp size={16} style={{ color: "#d97706" }} /> Medium
      </span>
    );
  }
  if (value === "Low") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowDown size={16} style={{ color: "#2563eb" }} /> Low
      </span>
    );
  }
  if (value === "Critical") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> Critical
      </span>
    );
  }
  if (value === "Normal") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <ArrowDown size={16} style={{ color: "#2563eb" }} /> Normal
      </span>
    );
  }
  return value;
};

const columns: TableColumn<Complaint>[] = [
  { key: "requestNo", header: "Complaint ID", type: "text" },
  {
    key: "utilitySupportRequest",
    header: "Complaint Name",
    render: (value: any) => {
      return `${value.name}`;
    },
  },

  {
    key: "supportRequestType",
    header: "Category/Sub Category",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.supportRequestType ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">
          {row.supportRequestSubtype ?? "NA"}
        </p>
      </div>
    ),
  },
  {
    key: "customerName",
    header: "Consumer",
    render: (value, row) => (
      <div>
        <p className="font-medium">{value ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">
          {row.consumerNo ?? "NA"}
        </p>
      </div>
    ),
  },
  {
    key: "statusDisplay",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) => {
        if (value === "Pending Approval") return "secondary";
        if (value === "Scheduled") return "outline";
        if (value === "In Progress") return "default";
        return "default";
      },
    },
  },
  {
    key: "severityLevel",
    header: "Priority",
    type: "custom",
    render: (value) => renderPriority(value),
  },
  {
    key: "createdDate",
    header: "Created On",
    render: (value, row) => (
      <div>
        <p className="font-medium">{value ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">
          {row.createdUserRemoteName ?? "NA"}
        </p>
      </div>
    ),
  },
  { key: "slaDueDate", header: "Due Date", type: "text" },
  {
    key: "slaStatus",
    header: "SLA Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) =>
        value === "Disconnection" ? "destructive" : "default",
    },
  },
];

export const CurrentList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Complaint | null>(null);
  const { mutateAsync: updateComplaintRequest } = useUpdateComplaintStatus(
    Number(remoteUtilityId)
  );

  const actions: TableAction<Complaint>[] = [
    {
      label: "View Details",
      onClick: (row) => navigate(`/cx/complaints/current/${row.id}`),
    },
    // { label: 'Assign to User', onClick: () => {} },
    // { label: 'Assign to Department', onClick: () => {} },
  ];

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await updateComplaintRequest({
        requestId: Number(selectedRow?.id),
        payload: {
          status: SERVICE_REQUEST_STATUS.REJECTED,
          closeDate: new Date().toISOString(),
          closureRemark:
            reason || "complaint request rejected by the consumer!",
        },
      });
      setRejectDialogOpen(false);
    } catch (error) {
      // toast.error("Failed to reject complaint request.");
    } finally {
      setIsRejecting(false);
    }
  };
  // Memoize the configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
        name: "name",
      },
      defaults: {
        page: 1,
        limit: 50,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
        name: "ALL_CATEGORIES",
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        name: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        name: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const exportFilters: ComplaintFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: filters.search_data,
      status: filters.status,
      category: filters.category,
      name: filters.name,
      // Note: No page and limit for export to get all data
    };
  }, [filters, remoteUtilityId]);

  const { exportCSV, isExporting } = useComplaintsExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const { data } = useCurrentComplaints({
    page: filters.page,
    limit: PAGE_SIZE,
    searchData: filters.search_data,
    status: filters.status,
    category: filters.category,
    remoteUtilityId: Number(remoteUtilityId),
    name: filters.name,
  });

  // Transform API data to match the table structure
  const transformedData: Complaint[] =
    data?.results?.map((item: Complaint) => ({
      id: item.id,
      slaSummary: item.slaSummary,
      contactNumber: item.contactNumber,
      email: item.email,
      category: item.category,
      subCategory: item.subCategory,
      consumer: item.consumer,
      remoteUtilityId: item.remoteUtilityId,
      utilitySupportRequest: item.utilitySupportRequest,
      requestNo: item.requestNo,
      status: item.status,
      statusDisplay: item.statusDisplay,
      requestType: item.requestType,
      createdDate: item.createdDate,
      createdUserRemoteName: item.createdUserRemoteName,
      consumerNo: item.consumerNo,
      firstName: item.firstName,
      lastName: item.lastName,
      requestDate: item.requestDate,
      customerName: item.firstName + " " + item.lastName,
      serviceAddress: {
        service: {
          hierarchyNames: {
            region: item.serviceAddress.service?.hierarchyNames?.region,
            country: item.serviceAddress.service?.hierarchyNames?.country,
            state: item.serviceAddress.service?.hierarchyNames?.state,
            county: item.serviceAddress.service?.hierarchyNames?.county,
            zone: item.serviceAddress.service?.hierarchyNames?.zone,
            division: item.serviceAddress.service?.hierarchyNames?.division,
            area: item.serviceAddress.service?.hierarchyNames?.area,
            subArea: item.serviceAddress.service?.hierarchyNames?.subArea,
            premise: item.serviceAddress.service?.hierarchyNames?.premise,
          },
          zipcode: item.serviceAddress.service?.zipcode,
          premise: item.serviceAddress.service?.premise,
          address: item.serviceAddress.service?.address,
          unit: item.serviceAddress.service?.unit,
        },
        billing: {
          hierarchyNames: {
            region: item.serviceAddress.billing?.hierarchyNames?.region,
            country: item.serviceAddress.billing?.hierarchyNames?.country,
            state: item.serviceAddress.billing?.hierarchyNames?.state,
            county: item.serviceAddress.billing?.hierarchyNames?.county,
            zone: item.serviceAddress.billing?.hierarchyNames?.zone,
            division: item.serviceAddress.billing?.hierarchyNames?.division,
            area: item.serviceAddress.billing?.hierarchyNames?.area,
            subArea: item.serviceAddress.billing?.hierarchyNames?.subArea,
            premise: item.serviceAddress.billing?.hierarchyNames?.premise,
          },
          zipcode: item.serviceAddress.billing?.zipcode,
          premise: item.serviceAddress.billing?.premise,
          address: item.serviceAddress.billing?.address,
          unit: item.serviceAddress.billing?.unit,
        },
      },
      utilitySupportName: item.utilitySupportName,
      severityLevel: item.severityLevel,
      slaDueDate: item.slaDueDate,
      timeRemaining: item.timeRemaining,
      slaStatus: item.slaStatus,
      address: item.serviceAddress.service?.address,
      categoryDisplay: item.categoryDisplay,
      subCategoryDisplay: item.subCategoryDisplay,
      supportRequestType: item.supportRequestType,
      supportRequestSubtype: item.supportRequestSubtype,
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  localStorage.removeItem("stepper-progress");
  const currentTab = 2;
  return (
    <>
      <GenericTable
        data={transformedData}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by name or ID..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={{
          currentPage: page,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: (page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(page * PAGE_SIZE, totalItems),
        }}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        onExport={handleExport} // Add this line
        exportLoading={isExporting} // Add this line
        filterPanel={
          <ComplaintFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            tab={currentTab}
          />
        }
      />
      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject complaint Request"
        description="Please provide a reason for rejecting this complaint request."
        label="Rejection Reason"
        placeholder="Please provide a reason for rejecting this request..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </>
  );
};
